import React, { useState } from 'react';
import API from '../services/api';
import './Comment.css';

function Comment({ comment, onReplySubmit, postOwnerId, currentUserId, acceptedCommentId, onAccept, onUnaccept, depth = 0 }) {
    const [showReplyForm, setShowReplyForm] = useState(false);
    const [replyText, setReplyText] = useState("");

    const [likes, setLikes] = useState(comment.upvote_count ?? comment.likes ?? comment.like_count ?? 0);
    const [isLiked, setIsLiked] = useState(comment.is_liked || false);

    const authorName = comment.author_username || comment.profiles?.username || comment.author || 'Anonim';
    const authorAvatar = comment.author_avatar || comment.profiles?.avatar_url || comment.authorAvatar || null;

    const isAccepted = comment.is_accepted_answer || (acceptedCommentId && comment.id === acceptedCommentId);
    const canAccept = Boolean(currentUserId && postOwnerId && currentUserId === postOwnerId);
    const isStarter = depth === 0;

    const formatDate = (dateString) => {
        if (!dateString) return comment.date || 'Şimdi';
        return new Date(dateString).toLocaleDateString('tr-TR', {
            day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
        });
    };

    const handleReplySubmit = (e) => {
        e.preventDefault();
        if (!replyText.trim()) return;
        if (onReplySubmit) onReplySubmit(comment.id, replyText);
        setReplyText("");
        setShowReplyForm(false);
    };

    const handleLikeClick = async () => {
        const nextLikedState = !isLiked;
        const diff = nextLikedState ? 1 : -1;
        setIsLiked(nextLikedState);
        setLikes(prev => prev + diff);
        try {
            await API.post('/votes', { target_type: 'comment', target_id: comment.id, vote_value: 1 });
        } catch (error) {
            setIsLiked(!nextLikedState);
            setLikes(prev => prev - diff);
        }
    };

    return (
        <div
            id={`comment-${comment.id}`}
            className="comment-container"
            style={{
                background: '#fff',
                border: isAccepted ? '1.5px solid var(--ok)' : '1px solid #e9ecef',
                borderRadius: '10px',
                padding: '14px 16px',
                marginBottom: '12px',
                boxShadow: '0 1px 2px rgba(0,0,0,0.04)'
            }}
        >
            <div className="comment-main">
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                    <img
                        src={authorAvatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(authorName)}&background=random&size=64`}
                        alt={authorName}
                        style={{ width: '36px', height: '36px', borderRadius: '50%', objectFit: 'cover', flexShrink: 0, marginTop: '2px' }}
                    />
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <div className="comment-header" style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                            {isStarter && (
                                <span style={{ background: 'var(--accent)', color: '#fff', fontSize: '11px', fontWeight: 700, padding: '2px 8px', borderRadius: '10px' }}>
                                    Başlatan
                                </span>
                            )}
                            <span className="comment-author">@{authorName}</span>
                            <span className="comment-date" style={{ color: 'var(--muted)', fontSize: '12px' }}>{formatDate(comment.created_at)}</span>

                            {isAccepted && (
                                <span className="accepted-answer-badge" style={{ color: 'var(--ok)', fontWeight: 'bold', fontSize: '13px' }}>
                                    ✓ Doğru Cevap
                                </span>
                            )}
                        </div>

                        <div className="comment-body" style={{ marginTop: '6px' }}>
                            {comment.body || comment.content || comment.text}

                            {comment.code_snippet && (
                                <pre className="comment-code-snippet" style={{ background: 'var(--code-bg)', color: '#d6dae2', padding: '10px', borderRadius: '6px', overflowX: 'auto', marginTop: '10px' }}>
                                    <code>{comment.code_snippet}</code>
                                </pre>
                            )}
                        </div>

                        <div className="comment-actions" style={{ display: 'flex', gap: '14px', marginTop: '10px' }}>
                            <button
                                className={`comment-btn like-btn ${isLiked ? 'liked' : ''}`}
                                onClick={handleLikeClick}
                                style={{ color: isLiked ? 'var(--danger)' : 'var(--muted)' }}
                            >
                                {isLiked ? '❤️' : '🤍'} {likes}
                            </button>

                            <button className="comment-btn reply-btn" onClick={() => setShowReplyForm(!showReplyForm)}>
                                ↩ Yanıtla
                            </button>

                            {canAccept && !isAccepted && (
                                <button className="comment-btn accept-btn" onClick={() => onAccept && onAccept(comment.id)} style={{ color: 'var(--ok)' }}>
                                    ✓ Cevap Olarak Kabul Et
                                </button>
                            )}
                            {canAccept && isAccepted && (
                                <button className="comment-btn accept-btn" onClick={() => onUnaccept && onUnaccept()} style={{ color: 'var(--danger)' }}>
                                    Kabulü Kaldır
                                </button>
                            )}
                        </div>

                        {showReplyForm && (
                            <form className="reply-input-form" onSubmit={handleReplySubmit} style={{ marginTop: '10px' }}>
                                <input
                                    type="text"
                                    placeholder={`@${authorName} kullanıcısına yanıt ver...`}
                                    value={replyText}
                                    onChange={(e) => setReplyText(e.target.value)}
                                    autoFocus
                                    style={{ width: '100%', padding: '8px 10px', borderRadius: '6px', border: '1px solid var(--line-strong)' }}
                                />
                                <div className="reply-form-btns" style={{ marginTop: '6px', display: 'flex', gap: '8px' }}>
                                    <button type="button" className="cancel-btn" onClick={() => setShowReplyForm(false)}>İptal</button>
                                    <button type="submit" className="submit-btn">Yanıtla</button>
                                </div>
                            </form>
                        )}
                    </div>
                </div>
            </div>

            {/* Yanıt zinciri — dikey çizgiyle görsel bağlantı */}
            {comment.replies && comment.replies.length > 0 && (
                <div className="replies-container" style={{ marginTop: '12px', marginLeft: '18px', paddingLeft: '18px', borderLeft: '2px solid var(--accent-soft)' }}>
                    {comment.replies.map((reply) => (
                        <Comment
                            key={reply.id}
                            comment={reply}
                            onReplySubmit={onReplySubmit}
                            postOwnerId={postOwnerId}
                            currentUserId={currentUserId}
                            acceptedCommentId={acceptedCommentId}
                            onAccept={onAccept}
                            onUnaccept={onUnaccept}
                            depth={depth + 1}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}

export default Comment;